import { Box, CircularProgress } from '@material-ui/core';
import { isEmpty, isEqual, isString, map } from 'lodash';
import { useObserver } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FB, FBEditorState, FBFieldName, FBSchemaComponentProps, FBSchemaProps, FBSchemaState, FBSortable, FBWorkspaceMode } from '..';
import SM from '../../../App/Shifamed/Utils/SM/SM';
import { intl } from '../../../common/intl';
import { formFieldsDisabledChangeControl, suppliersDisabledChangeControl } from '../../document.revision/utils/helpers';
import { isTranslation } from '../../translations/types';
import { DEVIATION_ADDED_BOX_PROPS } from '../FBDeviationProcedure/FBDeviationProcedure.constants';
import { getDeviationStepView } from '../FBDeviationProcedure/FBDeviationProcedure.helpers';
import FBDeviationToggle from '../FBDeviationProcedure/FBDeviationToggle';
import FBFieldOutputWrapper from '../FBFieldOutputWrapper/FBFieldOutputWrapper';
import { getSchemaRenderMetadata, isSchemaItemVisible } from './FBSchema.helpers';

const loaderRenderer = () => (
  <Box display="flex" justifyContent="center">
    <CircularProgress size={40} disableShrink />
  </Box>
);

const DESIGN_MODES: FBWorkspaceMode[] = [
  'design',
  'formDesign',
];

const PREVIEW_MODES: FBWorkspaceMode[] = [
  'preview',
  'formPreview',
];

interface FBSchemaItemRendererProps {
  schemaItem: FBSchemaProps
  index: number
  position: number
  stepIndex?: number
  autosave?: boolean
  disabled?: boolean
}

const FBSchemaItemRenderer: React.FC<FBSchemaItemRendererProps> = ({
  schemaItem,
  index,
  position,
  stepIndex,
  autosave,
  disabled = false,
}) => (
  <FBFieldOutputWrapper position={position} inputProps={schemaItem}>
    {FBSchemaState.component({
      autosave,
      ...schemaItem,
      ...(stepIndex !== undefined) && { stepIndex },
      disabled,
      gutter: false,
      index,
    })}
  </FBFieldOutputWrapper>
);

const FBSchemaItem = React.memo(FBSchemaItemRenderer, isEqual);

export const withFBSchema = <T extends FBSchemaComponentProps>(
  Component: React.FC<T>,
): React.FC<T> => {
  const Comp = ({
    workspaceMode,
    schemaState,
    schema,
    ...props
  }: T) => {
    schemaState = schemaState ?? FB.useRef<FBSchemaState>(FBSchemaState, schema);
    const { workspaceState, formState } = FB.useStores();
    const {
      isOutput = false,
      id,
      document: {
        outputRevision = false,
        isBeingEditedAfterRelease = false,
      } = {},
    } = workspaceState ?? {};
    const { _tabsState, _documentRevisionFormState } = SM.useStores();

    // To avoid UI-freezes on rendering huge list in schema.
    const [rendered, setRendered] = useState(false);
    useEffect(() => {
      const timeout = setTimeout(() => setRendered(true));
      return () => clearTimeout(timeout);
    }, []);

    const isBuildTypeChangeApprovalEnabled = useObserver<boolean>(() => workspaceState?.buildTypeChangeStatus() ?? false);
    const currentTabId = useObserver<string | undefined>(() => _tabsState?.activeTabId);
    // LHR step only (undefined for any other document): its deviation's switch and added components
    const deviationView = useObserver(() => getDeviationStepView(workspaceState));

    function isApprovalBlocking (item: FBSchemaProps): boolean {
      const { type, name = '' } = item;
      const activeConfig = type === 'inlineapproval' ? item : formState?.getFieldValue(name);
      const { approveIsBlocking = false } = activeConfig ?? {};
      const fieldToCheck = type === 'inlineapproval' ? name : `${FBFieldName.RequestApproval}${name}`;
      return Boolean(approveIsBlocking && !formState?.getFieldValue(fieldToCheck));
    }

    const isDesignOrFormDesign = workspaceMode !== undefined && DESIGN_MODES.includes(workspaceMode) && !isBeingEditedAfterRelease;

    function schemaRenderer (): React.ReactNode | React.ReactNode[] | undefined {
      const isPreviewOrFormPreview = workspaceMode !== undefined && PREVIEW_MODES.includes(workspaceMode);

      schema?.forEach(({ name, validationAttribute, label, placeholder }) => {
        const attrLabel = isString(label) ? label : placeholder ?? name;
        const attr = validationAttribute ?? attrLabel;
        const validationAttr = attr && isTranslation(attr) ? intl.formatMessage({ id: attr }) : attr;

        if (name && validationAttr) {
          formState?.setValidationAttributeName(name, validationAttr);
        }
      });

      if (isDesignOrFormDesign) {
        return <FBSortable />;
      }

      const workspaceFieldNames = new Set(workspaceState?.schema?.map((item) => item.name).filter(Boolean));
      const renderMetadata = getSchemaRenderMetadata(schema ?? [], FBEditorState.pickStepIndex, isApprovalBlocking);
      let position = 0;
      return map(schema, (schemaItem, index) => {
        const isUnderWorkspace = workspaceFieldNames.has(schemaItem.name);
        const { disabled = false, outputDisabled = false } = schemaItem || {};
        const reviseDisabled = schemaItem.editorProperties?.includes('reviseDisabled') ?? false;

        if (!isSchemaItemVisible(schemaItem, currentTabId, _documentRevisionFormState?.leftPanelOpen)) {
          return;
        }

        const isDisabledViaConfigOptions = schemaItem.name === 'part-vendors'
          ? suppliersDisabledChangeControl(workspaceState?.document)
          : formFieldsDisabledChangeControl(workspaceState?.document);

        position++;

        const { stepIndex, isBlocked } = renderMetadata[index];

        const isOutputDisabled = outputDisabled && isOutput && !isEmpty(id);
        const isReviseDisabled = reviseDisabled && outputRevision;

        const isDisabled = Boolean(
          disabled
          || isDisabledViaConfigOptions
          || isPreviewOrFormPreview
          || isBlocked
          || isReviseDisabled
          || isOutputDisabled
          || isBuildTypeChangeApprovalEnabled,
        );

        const shouldAutosave = Boolean(isUnderWorkspace && workspaceState?.autosave);

        const item = (
          <FBSchemaItem
            key={schemaItem.name}
            {...{ index, position, stepIndex }}
            // a copy only for deviation-added components, so every other item keeps its memo
            schemaItem={deviationView?.highlighted.has(schemaItem.name ?? '')
              ? { ...schemaItem, boxProps: DEVIATION_ADDED_BOX_PROPS } as FBSchemaProps
              : schemaItem}
            autosave={shouldAutosave}
            disabled={isDisabled}
          />
        );
        if (deviationView?.toggleAnchor !== schemaItem.name) { return item; }
        return (
          <React.Fragment key={schemaItem.name}>
            <FBDeviationToggle />
            {item}
          </React.Fragment>
        );
      });
    }

    useObserver(() => {
      schema = schemaState?.schema;
    });

    return Component({
      ...(props as T),
      schemaState,
      workspaceMode,
      schema,
      children: rendered ? schemaRenderer() : loaderRenderer(),
    });
  };

  return (props: T) => Comp(props);
};
